/**
 * Requirement detail — the report's §4 rendering, live (IA spec §6.2).
 *
 * Rules pinned by tests:
 *  - failed tests render with their observations — contradicting evidence is
 *    never suppressed in favor of the supporting set;
 *  - an authorization renders as a governance fact ADJACENT to the
 *    determination, carrying "did not convert incomplete data into complete
 *    data" — never as something that upgraded the evidence;
 *  - INSUFFICIENT renders the four-part required-input block with its link;
 *  - citations state artifact granularity plainly — no implied record-level
 *    precision;
 *  - the operations cross-link points INTO /ce (one direction; a structural
 *    test asserts no CE page links back into /compliance).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import api, { errorDetail } from '../services/api';
import { fmtTs } from '../utils/complianceFormat';
import { GlossChip } from './ComplianceRequirements';

export default function ComplianceRequirementDetail() {
  const { requirementId } = useParams();
  const [d, setD] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  const load = useCallback(async () => {
    setError(null);
    try {
      const { data } = await api.get(`/skills/compliance/requirements/${requirementId}`);
      if (!mounted.current) return;
      setD(data);
    } catch (err) {
      if (!mounted.current) return;
      setError(errorDetail(err, 'This requirement could not be loaded.'));
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [requirementId]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="bg-gray-900 text-gray-100 min-h-screen">
      <div className="p-6 max-w-4xl mx-auto">
      <Link to="/compliance/requirements" className="text-xs text-gray-400 underline">← Requirements</Link>
      {loading && <div className="text-gray-400 text-sm mt-4">Loading…</div>}
      {error && (
        <div data-testid="detail-error" className="bg-red-900/30 border border-red-800 rounded-xl p-5 text-sm text-red-200 mt-4">
          {error}
        </div>
      )}
      {!loading && !error && d && (
        <>
          <div className="flex items-start justify-between gap-4 mt-3 mb-1">
            <h1 className="text-xl font-bold text-white font-mono">{d.requirement_id}</h1>
            <GlossChip determination={d.determination} gloss={d.determination_gloss} />
          </div>
          <p className="text-sm text-gray-300">{d.title}</p>
          <p className="text-xs text-gray-500 mt-1 mb-4">{d.requirement_paraphrase}</p>

          {d.newer_inputs_exist && (
            <div className="bg-blue-900/30 border border-blue-800 rounded-lg p-3 text-xs text-blue-200 mb-4">
              Newer inputs exist — this determination updates on the next assessment run.
            </div>
          )}

          <h2 className="text-sm font-semibold text-gray-300 mb-1">Why it stands here</h2>
          <p className="text-sm text-gray-200 mb-1">{d.rationale}</p>
          <p className="text-xs text-gray-500 mb-4">Applicability: {d.applicability_basis}</p>

          {d.required_input && (
            <div data-testid="required-input" className="bg-amber-900/20 border border-amber-800 rounded-xl p-4 mb-4 text-sm">
              <div className="font-semibold text-amber-200 mb-2">Not concluded — input required</div>
              <ol className="text-xs text-gray-300 space-y-1 list-decimal list-inside">
                <li><span className="text-gray-400">What was checked:</span> {d.required_input.what_was_checked}</li>
                <li><span className="text-gray-400">What is missing:</span> {d.required_input.what_is_missing}</li>
                <li><span className="text-gray-400">Who can supply it:</span> {d.required_input.who_can_supply_it}</li>
                <li><span className="text-gray-400">What it becomes:</span> {d.required_input.what_it_becomes}</li>
              </ol>
              <Link to="/compliance/inputs" className="text-xs text-amber-300 underline mt-2 inline-block">
                Supply it on the Inputs screen
              </Link>
            </div>
          )}

          <h2 className="text-sm font-semibold text-gray-300 mb-2">Tests ({d.tests_summary?.summary})</h2>
          <div className="space-y-2 mb-4">
            {d.tests.map((t) => (
              <div key={t.test_id} data-testid={`test-${t.test_id}`}
                   className={`rounded-lg border p-3 text-xs ${
                     t.passed === false ? 'border-red-800 bg-red-900/20 text-red-200'
                     : t.passed === null ? 'border-amber-800 bg-amber-900/10 text-amber-200'
                     : 'border-gray-700 bg-gray-800 text-gray-300'}`}>
                <span className="font-mono">{t.test_id}</span>
                <span className="ml-2">{t.passed === true ? 'passed' : t.passed === false ? 'FAILED' : 'indeterminate'}</span>
                <div className="mt-1 text-gray-300">{t.observation}</div>
              </div>
            ))}
          </div>

          {d.uncertainties.length > 0 && (
            <div className="mb-4">
              <h2 className="text-sm font-semibold text-gray-300 mb-1">Uncertainties</h2>
              {d.uncertainties.map((u, i) => (
                <p key={i} className="text-xs text-gray-400 italic mb-1">{u}</p>
              ))}
            </div>
          )}

          <h2 className="text-sm font-semibold text-gray-300 mb-1">
            Citations <span className="text-xs text-gray-500 font-normal">(granularity: {d.citation_granularity} — record-level precision is not implied)</span>
          </h2>
          <div className="overflow-x-auto mb-4">
            <table className="w-full text-xs">
              <thead><tr className="text-left text-gray-500 border-b border-gray-700">
                <th className="py-1 pr-3">Artifact</th><th className="py-1 pr-3">Type</th><th className="py-1">Checksum</th>
              </tr></thead>
              <tbody>
                {d.citations.map((c) => (
                  <tr key={c.artifact_id} className="border-b border-gray-800 text-gray-300">
                    <td className="py-1 pr-3 font-mono">{c.artifact_id}</td>
                    <td className="py-1 pr-3">{c.artifact_type}</td>
                    <td className="py-1 font-mono">{(c.checksum || '—').slice(0, 16)}…</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {d.findings.length > 0 && (
            <div className="mb-4" data-testid="linked-findings">
              <h2 className="text-sm font-semibold text-gray-300 mb-2">Linked findings</h2>
              {d.findings.map((f) => (
                <div key={f.finding_id} className="bg-red-900/20 border border-red-800 rounded-lg p-3 text-xs text-red-200 mb-2">
                  <span className="font-semibold">{f.condition}</span>
                  <span className="ml-2 text-red-300">({f.severity})</span>
                  <Link to="/compliance/findings" className="ml-2 underline">Findings screen</Link>
                </div>
              ))}
            </div>
          )}

          {d.evidence_provenance.length > 0 && (
            <div className="mb-4">
              <h2 className="text-sm font-semibold text-gray-300 mb-2">Evidence provenance</h2>
              {d.evidence_provenance.map((p) => (
                <div key={p.bundle_id} data-testid={`prov-${p.bundle_id}`}
                     className="bg-gray-800 rounded-xl border border-gray-700 p-4 text-xs text-gray-300 mb-2">
                  <div>Bundle <span className="font-mono">{p.bundle_id.slice(0, 8)}</span> · sealed hash <span className="font-mono">{(p.sealed_snapshot_hash || '—').slice(0, 16)}…</span></div>
                  <div className="mt-1">
                    Completeness: {p.complete ? 'complete' : 'a named category is short or coverage could not be established'}
                    {p.derivation_coverage_status && ` · derivation coverage: ${p.derivation_coverage_status}`}
                  </div>
                  {p.authorization && (
                    <div data-testid={`auth-${p.bundle_id}`} className="mt-2 border-l-2 border-gray-600 pl-2 text-gray-400">
                      Authorized by {p.authorization.authorized_by} at {fmtTs(p.authorization.authorized_at)} — {p.authorization.statement}
                    </div>
                  )}
                  <a href={p.operations_link} className="text-blue-300 underline mt-2 inline-block">
                    Collector &amp; sensor health (operations)
                  </a>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
    </div>
  );
}
